import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { authAPI, patientAPI, reportAPI, imagingAPI, excelAPI, normalizePatient, normalizeReport, setToken, clearToken } from "../utils/api";

const AppContext = createContext();

const TOKEN_KEY    = "emr_token";
const USER_KEY     = "emr_user";
const REMEMBER_KEY = "emr_remember_device";

const IDLE_TIMEOUTS = {
  admin:        30 * 60 * 1000,
  doctor:       60 * 60 * 1000,
  technician:   null,
  receptionist: null,
};

const PIN_ROLES      = ["admin", "doctor"];
const REMEMBER_ROLES = ["technician", "receptionist"];

export function AppProvider({ children }) {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem(USER_KEY)) || null; } catch { return null; }
  });
  const [patients, setPatients]             = useState([]);
  const [loading, setLoading]               = useState(false);
  const [initialLoad, setInitialLoad]       = useState(() => !!localStorage.getItem("emr_token"));
  const [matchedReportId, setMatchedReportId] = useState(null);
  const [error, setError]                   = useState(null);
  const [backendOk, setBackendOk]           = useState(null);
  const [serverStatus, setServerStatus]     = useState("ok"); // "ok" | "slow" | "down"
  const serverRetryRef                      = useRef(null);
  const [pagination, setPagination]         = useState({ page: 1, total: 0, total_pages: 1 });
  const [searchQuery, setSearchQuery]       = useState("");
  const [sessionExpired, setSessionExpired] = useState(false);
  const [maintenance, setMaintenance]       = useState(false);
  const [justUnlocked, setJustUnlocked]     = useState(false);
  const [pinLocked, setPinLocked]           = useState(false);

  const logoutTimer = useRef(null);
  const idleTimer   = useRef(null);
  const verifyPollRef = useRef(null);

  // ── Backend health check ──────────────────────────────────────────────────
  useEffect(() => {
    const checkMaintenance = () => {
      fetch((window.location.hostname === "localhost" ? "http://localhost:8000" : "https://nashman-prod-production-c773.up.railway.app") + "/backup/maintenance")
        .then(r => r.json())
        .then(data => setMaintenance(data.maintenance || false))
        .catch(() => {});
    };
    checkMaintenance();
    // Random 0-10s stagger so 4 users don't all poll at the exact same second
    const stagger = Math.floor(Math.random() * 10000);
    let maintenanceInterval;
    const staggerTimeout = setTimeout(() => {
      checkMaintenance();
      maintenanceInterval = setInterval(checkMaintenance, 30000);
    }, stagger + 30000);

    const HEALTH_URL = (window.location.hostname === "localhost" ? "http://localhost:8000" : "https://nashman-prod-production-c773.up.railway.app") + "/health";

    const checkHealth = () => {
      const start = Date.now();
      fetch(HEALTH_URL, { signal: AbortSignal.timeout(8000) })
        .then(r => {
          const ms = Date.now() - start;
          if (r.ok) {
            setBackendOk(true);
            setServerStatus(ms > 8000 ? "slow" : "ok");
            if (serverRetryRef.current) {
              clearTimeout(serverRetryRef.current);
              serverRetryRef.current = null;
            }
          } else {
            setBackendOk(false);
            setServerStatus("down");
          }
        })
        .catch(() => {
          setBackendOk(false);
          setServerStatus("down");
          console.error("[EMR] Backend health check failed — server may be restarting");
          // Exponential backoff: 15s, 30s, 60s — prevents flooding a restarting worker
          if (!serverRetryRef.current) {
            let retryDelay = 15000; // start at 15s
            const retry = () => {
              serverRetryRef.current = setTimeout(() => {
                fetch(HEALTH_URL, { signal: AbortSignal.timeout(5000) })
                  .then(r => {
                    if (r.ok) {
                      setBackendOk(true);
                      setServerStatus("ok");
                      serverRetryRef.current = null;
                      console.log("[EMR] Backend is back online");
                    } else {
                      retryDelay = Math.min(retryDelay * 2, 60000);
                      retry();
                    }
                  })
                  .catch(() => {
                    retryDelay = Math.min(retryDelay * 2, 60000);
                    retry();
                  });
              }, retryDelay + Math.random() * 5000); // add jitter
            };
            retry();
          }
        });
    };
    checkHealth();
    return () => {
      clearTimeout(staggerTimeout);
      if (maintenanceInterval) clearInterval(maintenanceInterval);
      if (serverRetryRef.current) clearTimeout(serverRetryRef.current);
    };
  }, []);

  // ── IP block polling — checks every 10s if user is logged in ────────────────
  // If backend returns 403 (IP blocked), forces immediate logout
  useEffect(() => {
    if (!user) {
      if (verifyPollRef.current) { clearInterval(verifyPollRef.current); verifyPollRef.current = null; }
      return;
    }
    if (verifyPollRef.current) clearInterval(verifyPollRef.current);
    // Random 0-30s stagger so multiple users don't all poll simultaneously
    const pollStagger = Math.floor(Math.random() * 30000);
    const startPoll = () => {
      verifyPollRef.current = setInterval(async () => {
      // Skip poll during PIN lock — server is already under load
      if (pinLocked) return;
      const tok = localStorage.getItem("emr_token");
      if (!tok) return;
      try {
        const controller = new AbortController();
        const tid = setTimeout(() => controller.abort(), 5000); // 5s — fail fast, don't queue
        const BASE_URL = window.location.hostname === "localhost"
          ? "http://localhost:8000"
          : "https://nashman-prod-production-c773.up.railway.app";
        const res = await fetch(`${BASE_URL}/auth/verify`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token: tok }),
          signal: controller.signal,
        });
        clearTimeout(tid);
        if (res.status === 403) {
          clearInterval(verifyPollRef.current);
          verifyPollRef.current = null;
          doLogout();
        }
      } catch (e) {
        // Timeout or network error — skip this cycle silently, stay logged in
      }
    }, 60000); // 60s interval
    };
    const staggerTimer = setTimeout(startPoll, pollStagger);
    return () => {
      clearTimeout(staggerTimer);
      if (verifyPollRef.current) clearInterval(verifyPollRef.current);
    };
  }, [user, pinLocked]);

  // ── Role-based idle timeout ───────────────────────────────────────────────
  // Uses both setTimeout (for normal in-browser inactivity) AND the Page
  // Visibility API (for PC sleep / tab switching / browser minimize).
  // When the PC wakes up or the tab becomes visible again, we check how long
  // the user was actually away and lock if it exceeded the timeout threshold.
  const lastActiveRef = useRef(Date.now());

  const resetIdleTimer = useCallback(() => {
    if (!user) return;
    const timeout = IDLE_TIMEOUTS[user.role];
    if (!timeout) return;
    lastActiveRef.current = Date.now();
    if (idleTimer.current) clearTimeout(idleTimer.current);
    idleTimer.current = setTimeout(() => {
      if (PIN_ROLES.includes(user.role)) {
        setPinLocked(true);
      } else {
        setSessionExpired(true);
        doLogout();
      }
    }, timeout);
  }, [user]);

  useEffect(() => {
    if (!user) {
      if (idleTimer.current) clearTimeout(idleTimer.current);
      return;
    }
    const timeout = IDLE_TIMEOUTS[user.role];
    if (!timeout) return;
    const events = ["mousedown", "mousemove", "keydown", "scroll", "touchstart", "click"];
    const onActivity = () => resetIdleTimer();
    events.forEach(e => window.addEventListener(e, onActivity, { passive: true }));
    resetIdleTimer();

    // Page Visibility API — fires when tab/window becomes visible again
    // after PC sleep, tab switch, or browser minimize. JS timers pause
    // during sleep so we manually check elapsed time on wake.
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        const elapsed = Date.now() - lastActiveRef.current;
        const timeoutMs = IDLE_TIMEOUTS[user?.role];

        // 1. Check idle timeout — has the user been away too long?
        if (timeoutMs && elapsed >= timeoutMs) {
          if (PIN_ROLES.includes(user?.role)) {
            setPinLocked(true);
            return;
          } else {
            setSessionExpired(true);
            doLogout();
            return;
          }
        }

        // 2. Verify the JWT is still valid with the backend — catches
        //    expired tokens that the paused setTimeout missed.
        //    Uses 5s timeout — fail fast, never block the event loop on wake.
        const token = localStorage.getItem("emr_token");
        if (token) {
          const BASE_URL = window.location.hostname === "localhost"
            ? "http://localhost:8000"
            : "https://nashman-prod-production-c773.up.railway.app";
          const controller = new AbortController();
          const tid = setTimeout(() => controller.abort(), 5000);
          fetch(`${BASE_URL}/auth/verify`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ token }),
            signal: controller.signal,
          }).then(async res => {
            clearTimeout(tid);
            if (res.status === 401) { setSessionExpired(true); doLogout(); }
          }).catch((err) => {
            clearTimeout(tid);
            // Timeout or network error — stay logged in, don't block
          });
        }
      } else {
        // Tab/window hidden — record the last active time
        lastActiveRef.current = Date.now();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      events.forEach(e => window.removeEventListener(e, onActivity));
      document.removeEventListener("visibilitychange", onVisibilityChange);
      if (idleTimer.current) clearTimeout(idleTimer.current);
    };
  }, [user, resetIdleTimer]);

  // ── Auto logout when JWT expires ─────────────────────────────────────────
  const scheduleAutoLogout = useCallback((expiresInSeconds) => {
    if (logoutTimer.current) clearTimeout(logoutTimer.current);
    const ms = Math.max(0, (expiresInSeconds - 30) * 1000);
    logoutTimer.current = setTimeout(() => {
      setSessionExpired(true);
      doLogout();
    }, ms);
  }, []);

  // ── Token verification on mount ───────────────────────────────────────────
  // Delayed 3 seconds to handle Electron cold-start: when the app opens,
  // the backend may still be waking up. A network error (Failed to fetch)
  // during that window would previously trigger doLogout() and wipe the
  // remembered session. Now we only logout on definitive auth failures
  // (401/expired/invalid), never on transient network errors.
  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token && user) {
      const timer = setTimeout(() => {
        authAPI.verifyToken(token)
          .then(res => { if (!res.valid) doLogout(); })
          .catch((err) => {
            const msg = err?.message || "";
            // Only force logout on definitive auth failures
            if (msg.includes("401") || msg.includes("expired") || msg.includes("Invalid token")) {
              doLogout();
            }
            // Network errors ("Failed to fetch") = backend still waking up,
            // ignore silently so remembered sessions survive cold starts
          });
      }, 3000); // 3s delay for Electron cold start
      return () => {
        clearTimeout(timer);
        if (logoutTimer.current) clearTimeout(logoutTimer.current);
      };
    }
    return () => { if (logoutTimer.current) clearTimeout(logoutTimer.current); };
  }, []);

  // ── Cross-tab session sync ────────────────────────────────────────────────
  useEffect(() => {
    let bc;
    try {
      bc = new BroadcastChannel("emr_session");
      bc.onmessage = (e) => {
        if (e.data === "logout") { setUser(null); setPatients([]); }
        else if (e.data === "login") {
          try { const u = JSON.parse(localStorage.getItem(USER_KEY)); if (u) setUser(u); } catch {}
        }
      };
    } catch {}
    const onStorage = (e) => {
      if (e.key === TOKEN_KEY && !e.newValue) { setUser(null); setPatients([]); }
      if (e.key === USER_KEY && e.newValue && !e.oldValue) {
        try { const u = JSON.parse(e.newValue); if (u) setUser(u); } catch {}
      }
    };
    window.addEventListener("storage", onStorage);
    return () => { window.removeEventListener("storage", onStorage); try { bc?.close(); } catch {} };
  }, []);

  // ── Load patients on login ────────────────────────────────────────────────
  const loadPatients = useCallback(async (page = 1, search = "") => {
    try {
      setLoading(true);
      const data = await patientAPI.list(search, page, 20);
      const pts = data.patients || [];
      if (pts.length === 0 && page > 1 && data.total > 0) {
        const p1 = await patientAPI.list(search, 1, 20);
        setPatients((p1.patients || []).map(normalizePatient));
        setPagination({ page: 1, total: p1.total, total_pages: p1.total_pages });
        setSearchQuery(search);
        return;
      }
      setPatients(pts.map(normalizePatient));
      setPagination({ page: data.page, total: data.total, total_pages: data.total_pages });
      setSearchQuery(search);
      if (search && search.trim().startsWith('#')) {
        setMatchedReportId(data.matched_report_id || null);
      } else {
        setMatchedReportId(null);
      }
      return data; // return so callers can access matched_report_id directly
    } catch (e) {
      if (e.message?.includes("expired") || e.message?.includes("401")) { setSessionExpired(true); doLogout(); }
      setError(e.message);
    } finally { setLoading(false); setInitialLoad(false); }
  }, []);

  const loadAllPatients = useCallback(async (search = "") => {
    const data = await patientAPI.listAll(search);
    return data.map(normalizePatient);
  }, []);

  useEffect(() => {
    if (user && backendOk && !justUnlocked) {
      // Small random stagger so multiple simultaneous reloads don't all hit DB at once
      const delay = Math.floor(Math.random() * 1500);
      const t = setTimeout(() => loadPatients(1, ""), delay);
      return () => clearTimeout(t);
    }
  }, [user, backendOk, loadPatients, justUnlocked]);

  // ── AUTH ──────────────────────────────────────────────────────────────────
  const login = async (username, password, rememberedToken = null, rememberMe = false) => {
    try {
      let res;
      if (rememberedToken) {
        // Restore session from stored long-lived token — no password needed
        const verified = await authAPI.verifyToken(rememberedToken);
        res = { role: verified.role, username: verified.username, token: rememberedToken, expires_in: null };
      } else {
        // Normal login — pass rememberMe so backend issues 90-day token (recep/tech) or 30-day token if checked
        res = await authAPI.login(username, password, rememberMe);
      }

      // Admin with MFA enabled — password was correct but we're not logged in yet.
      // Return the temp_token so the UI can show the 6-digit code screen.
      if (res.mfa_required) {
        return { success: false, mfaRequired: true, tempToken: res.temp_token, expiresIn: res.expires_in };
      }

      if (res.role !== "admin") {
        const mRes = await fetch(
          (window.location.hostname === "localhost" ? "http://localhost:8000" : "https://nashman-prod-production-c773.up.railway.app") + "/backup/maintenance"
        ).then(r => r.json()).catch(() => ({ maintenance: false }));
        if (mRes.maintenance) return { success: false, error: "System is under maintenance. Only administrators can access." };
      }
      const u = { role: res.role, username: res.username };
      if (res.token) setToken(res.token);
      setUser(u);
      setPinLocked(false);
      setSessionExpired(false);
      localStorage.setItem(USER_KEY, JSON.stringify(u));
      ["emr_pd_view","emr_pd_type","emr_pd_rep","emr_pd_param","emr_selected_patient"].forEach(k => sessionStorage.removeItem(k));
      if (res.expires_in) scheduleAutoLogout(res.expires_in);
      try { new BroadcastChannel("emr_session").postMessage("login"); } catch {}
      return { success: true, token: res.token };
    } catch (e) { return { success: false, error: e.message }; }
  };

  // Completes login after the user enters their 6-digit authenticator code.
  const loginMfa = async (tempToken, code, rememberMe = false) => {
    try {
      const res = await authAPI.loginMfa(tempToken, code, rememberMe);
      const u = { role: res.role, username: res.username };
      if (res.token) setToken(res.token);
      setUser(u);
      setPinLocked(false);
      setSessionExpired(false);
      localStorage.setItem(USER_KEY, JSON.stringify(u));
      ["emr_pd_view","emr_pd_type","emr_pd_rep","emr_pd_param","emr_selected_patient"].forEach(k => sessionStorage.removeItem(k));
      if (res.expires_in) scheduleAutoLogout(res.expires_in);
      try { new BroadcastChannel("emr_session").postMessage("login"); } catch {}
      return { success: true, token: res.token };
    } catch (e) { return { success: false, error: e.message }; }
  };

  const doLogout = () => {
    setUser(null);
    setPatients([]);
    setPinLocked(false);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(TOKEN_KEY);
    if (logoutTimer.current) clearTimeout(logoutTimer.current);
    if (idleTimer.current)   clearTimeout(idleTimer.current);
    try { new BroadcastChannel("emr_session").postMessage("logout"); } catch {}
  };

  const logout = () => {
    localStorage.removeItem(REMEMBER_KEY);
    setSessionExpired(false);
    doLogout();
  };

  // ── PIN lock ──────────────────────────────────────────────────────────────
  const unlockWithPin = async (pin) => {
    if (!user) return { success: false, error: "No user" };
    try {
      await authAPI.verifyPin(pin, user.role);
      resetIdleTimer();
      setJustUnlocked(true);
      setPinLocked(false);
      // Clear justUnlocked after pages have had time to settle
      setTimeout(() => setJustUnlocked(false), 3000);
      return { success: true };
    } catch (e) {
      return { success: false, error: e.message };
    }
  };

  // ── Remember device ───────────────────────────────────────────────────────
  const saveRememberDevice = (username, token) => {
    const t       = token || localStorage.getItem(TOKEN_KEY);
    const expires = Date.now() + 30 * 24 * 60 * 60 * 1000;
    localStorage.setItem(REMEMBER_KEY, JSON.stringify({ username, token: t, expires }));
  };

  const getRememberedDevice = () => {
    try {
      const saved = JSON.parse(localStorage.getItem(REMEMBER_KEY));
      if (!saved) return null;
      if (Date.now() > saved.expires) { localStorage.removeItem(REMEMBER_KEY); return null; }
      if (saved.password && !saved.token) { localStorage.removeItem(REMEMBER_KEY); return null; }
      return saved;
    } catch { return null; }
  };

  const clearRememberedDevice = () => localStorage.removeItem(REMEMBER_KEY);

  // ── Password / auth helpers ───────────────────────────────────────────────
  const changePassword   = async (role, newPassword, editorRole) => authAPI.changePassword(role, newPassword, editorRole || user?.role);
  const verifyMasterCode = async (code, newPassword) => { try { await authAPI.emergencyReset(code, newPassword, "admin"); return { success: true }; } catch (e) { return { success: false, error: e.message }; } };
  const updateMasterCode = async (current, newCode)  => { try { await authAPI.updateMasterCode(current, newCode); return { success: true }; } catch (e) { return { success: false, error: e.message }; } };

  // ── MFA (two-factor authentication) ────────────────────────────────────────
  const mfaSetup       = async ()                 => { try { const res = await authAPI.mfaSetup(); return { success: true, secret: res.secret, qrCodePng: res.qr_code_png }; } catch (e) { return { success: false, error: e.message }; } };
  const mfaVerifySetup = async (code)              => { try { const res = await authAPI.mfaVerifySetup(code); return { success: true, backupCodes: res.backup_codes || [] }; } catch (e) { return { success: false, error: e.message }; } };
  const mfaDisable     = async (code)              => { try { await authAPI.mfaDisable(code); return { success: true }; } catch (e) { return { success: false, error: e.message }; } };
  const mfaStatus      = async ()                  => { try { const res = await authAPI.mfaStatus(); return { success: true, enabled: res.mfa_enabled, backupCodesRemaining: res.backup_codes_remaining ?? 0 }; } catch (e) { return { success: false, error: e.message }; } };
  const mfaRegenerateBackupCodes = async (code)    => { try { const res = await authAPI.mfaRegenerateBackupCodes(code); return { success: true, backupCodes: res.backup_codes || [] }; } catch (e) { return { success: false, error: e.message }; } };
  const getAuditLog      = async (limit = 500, period = "today") => authAPI.getAuditLog(limit, period);
  const forgotPassword   = async (email)              => authAPI.forgotPassword(email);
  const resetPassword    = async (token, newPassword) => authAPI.resetPassword(token, newPassword);
  const saveAdminEmail   = async (email)              => authAPI.saveAdminEmail(email);
  const getAdminEmail    = async ()                   => authAPI.getAdminEmail();

  // ── PATIENTS ──────────────────────────────────────────────────────────────
  const addPatient = async (data) => {
    const created = await patientAPI.create(data);
    await loadPatients(1, "");
    return normalizePatient(created).id;
  };

  const deletePatient = async (id) => {
    setPatients(prev => prev.filter(p => p.id !== id));
    setPagination(prev => ({ ...prev, total: Math.max(0, (prev.total || 1) - 1) }));
    try { await patientAPI.delete(id); }
    catch (err) { await loadPatients(pagination.page, searchQuery); throw err; }
  };

  const updatePatient = async (id, data) => {
    const updated    = await patientAPI.update(id, data);
    const normalized = normalizePatient(updated);
    setPatients(prev => prev.map(p => p.id === id ? normalized : p));
  };

  const refreshPatient = async (id) => {
    const fresh      = await patientAPI.get(id);
    const normalized = normalizePatient(fresh);
    setPatients(prev => prev.map(p => p.id === id ? normalized : p));
    return normalized;
  };

  const searchPatients = async (query) => {
    const data = await patientAPI.list(query);
    return data.map(normalizePatient);
  };

  // ── REPORTS ───────────────────────────────────────────────────────────────
  const addReport = async (patientId, { file, type, lab, labId, date, time, patientSex, patientAge }) => {
    const formData = new FormData();
    formData.append("patient_id",  patientId);
    formData.append("report_type", type);
    formData.append("lab",         lab || "");
    formData.append("lab_id",      labId || "");
    formData.append("date",        date || "");
    formData.append("time",        time || "");
    formData.append("patient_sex", patientSex || "Female");
    formData.append("patient_age", patientAge != null ? String(patientAge) : "");
    formData.append("file",        file);
    const result     = await reportAPI.upload(formData);
    const normalized = normalizeReport(result);
    setPatients(prev => prev.map(p => p.id !== patientId ? p : { ...p, reports: [...p.reports, normalized] }));
    return { report: normalized, extractionError: result.extraction_error, extractionNote: result.extraction_note, extractionMethod: result.extraction_method };
  };

  // ── Two-phase upload: extract only (no DB/R2 write) ───────────────────────
  const extractReport = async (patientId, { file, type, lab, labId, date, time, patientSex, patientAge }) => {
    const formData = new FormData();
    formData.append("patient_id",  patientId);
    formData.append("report_type", type);
    formData.append("lab",         lab || "");
    formData.append("lab_id",      labId || "");
    formData.append("date",        date || "");
    formData.append("time",        time || "");
    formData.append("patient_sex", patientSex || "Female");
    formData.append("patient_age", patientAge != null ? String(patientAge) : "");
    formData.append("file",        file);
    const result = await reportAPI.extract(formData);
    // Returns { file_key, values, report_type, lab, lab_id, date, time, extraction_error, ... }
    return result;
  };

  // ── Two-phase upload: save to R2 + DB (called on Save Report click) ───────
  const saveReport = async (patientId, { fileKey, originalFilename, type, lab, labId, date, time, values, extractionMethod }) => {
    const backendValues = values.map(v => ({
      name:       v.name,
      value:      typeof v.value === "number" ? v.value : (isNaN(parseFloat(v.value)) ? null : parseFloat(v.value)),
      value_text: v.valueText || String(v.value ?? ""),
      unit:       v.unit,
      ref_min:    v.refMin ?? v.ref_min,
      ref_max:    v.refMax ?? v.ref_max,
      ref_text:   v.refText || v.ref_text || "",
      confidence: v.confidence || "high",
      status:     v.status || "Needs Review",
      abnormal:   v.abnormal,
    }));
    const result     = await reportAPI.save({
      file_key:          fileKey,
      original_filename: originalFilename,
      patient_id:        patientId,
      report_type:       type,
      lab:               lab || "",
      lab_id:            labId || "",
      date:              date || "",
      time:              time || "",
      extraction_method: extractionMethod || "",
      values:            backendValues,
    });
    const normalized = normalizeReport(result);
    // Refresh patient data from API to get re-enriched status values
    try {
      const fresh = await patientAPI.get(patientId, 1, 20);
      if (fresh) {
        const freshNorm = normalizePatient(fresh);
        setPatients(prev => prev.map(p => p.id !== patientId ? p : freshNorm));
        const freshRep = freshNorm.reports?.find(r => r.id === normalized.id);
        return freshRep || normalized;
      }
    } catch (e) {
      // fallback to normalized from save response
    }
    setPatients(prev => prev.map(p => p.id !== patientId ? p : { ...p, reports: [...p.reports, normalized] }));
    return normalized;
  };

  const editReport = async (patientId, reportId, updatedValues, editorRole) => {
    const backendValues = updatedValues.map(v => ({
      name: v.name, value: typeof v.value === "number" ? v.value : (isNaN(parseFloat(v.value)) ? null : parseFloat(v.value)),
      value_text: v.valueText || String(v.value ?? ""), unit: v.unit,
      ref_min: v.refMin, ref_max: v.refMax, ref_text: v.refText || v.ref_text || "",
      confidence: v.confidence || "high",
      status: v.status || "Needs Review", abnormal: v.abnormal,
    }));
    const updated    = await reportAPI.edit(reportId, backendValues, editorRole);
    const normalized = normalizeReport(updated);
    setPatients(prev => prev.map(p => p.id !== patientId ? p : { ...p, reports: p.reports.map(r => r.id === reportId ? normalized : r) }));
    return normalized;
  };

  const deleteReport = async (patientId, reportId) => {
    await reportAPI.delete(reportId);
    setPatients(prev => prev.map(p => p.id !== patientId ? p : { ...p, reports: p.reports.filter(r => r.id !== reportId) }));
  };

  // ── IMAGING ───────────────────────────────────────────────────────────────
  const addImaging = async (patientId, data, file) => {
    const formData = new FormData();
    formData.append("patient_id",  patientId);
    formData.append("modality",    data.modality || "X-Ray");
    formData.append("date",        data.date || "");
    formData.append("time",        data.time || "");
    formData.append("description", data.description || "");
    formData.append("findings",    data.findings || "");
    formData.append("radiologist", data.radiologist || "");
    formData.append("created_by",  user?.role || "");
    formData.append("file",        file);
    return await imagingAPI.upload(formData);
  };

  const updateImaging     = async (id, data) => imagingAPI.update(id, data);
  const deleteImaging     = async (id)       => imagingAPI.delete(id);
  const getPatientImaging = async (patientId) => imagingAPI.getForPatient(patientId);
  const openImagingFile = async (id) => {
    try {
      const { token: dtok } = await authAPI.getDownloadToken();
      window.open(`${imagingAPI.fileUrl(id)}?dtok=${dtok}`, "_blank");
    } catch (e) {
      console.error("Could not get download token", e);
    }
  };
  // Legacy alias — returns base URL without token; use openImagingFile for browser opens
  const getImagingFileUrl = (id) => imagingAPI.fileUrl(id);

  // ── EXCEL ─────────────────────────────────────────────────────────────────
  const exportToExcel = async (patient) => {
    try {
      const { token: dtok } = await authAPI.getDownloadToken();
      window.open(`${excelAPI.exportUrl(patient.id)}?dtok=${dtok}`, "_blank");
    } catch (e) {
      console.error("Could not get download token", e);
    }
  };

  const importFromExcel = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    return await excelAPI.import(formData);
  };

  const importBatch = async (files) => {
    const formData = new FormData();
    files.forEach(f => formData.append("files", f));
    const result = await excelAPI.importBatch(formData);
    setPatients([]); setPagination({ page: 1, total: 0, total_pages: 1 }); setSearchQuery("");
    await loadPatients(1, "");
    return result;
  };

  const openPdfUrl = async (reportId) => {
    // Check if report has a direct storage URL (R2 public — served via presigned in future)
    const patient = patients.find(p => p.reports?.some(r => r.id === reportId));
    const report  = patient?.reports?.find(r => r.id === reportId);
    if (report?.storageUrl) { window.open(report.storageUrl, "_blank"); return; }
    // Get a one-time download token (90s, single-use) — never put main JWT in URL
    try {
      const { token: dtok } = await authAPI.getDownloadToken();
      window.open(`${reportAPI.pdfUrl(reportId)}?dtok=${dtok}`, "_blank");
    } catch (e) {
      console.error("Could not get download token", e);
    }
  };

  return (
    <AppContext.Provider value={{
      user, login, loginMfa, logout,
      patients, loading, initialLoad, matchedReportId, error, backendOk, serverStatus, sessionExpired,
      pagination, searchQuery,
      loadPatients, loadAllPatients, searchPatients, refreshPatient,
      addPatient, deletePatient, updatePatient,
      addReport, extractReport, saveReport, editReport, deleteReport,
      addImaging, updateImaging, deleteImaging, getPatientImaging, getImagingFileUrl, openImagingFile,
      changePassword, verifyMasterCode, updateMasterCode, getAuditLog,
      mfaSetup, mfaVerifySetup, mfaDisable, mfaStatus, mfaRegenerateBackupCodes,
      forgotPassword, resetPassword, saveAdminEmail, getAdminEmail,
      exportToExcel, importFromExcel, importBatch,
      openPdfUrl,
      maintenance, setMaintenance,
      pinLocked, setPinLocked, unlockWithPin, justUnlocked,
      saveRememberDevice, getRememberedDevice, clearRememberedDevice,
    }}>
      {children}
    </AppContext.Provider>
  );
}

export const useApp = () => useContext(AppContext);